import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';

export function Newsletter() {
  const isAdmin = useIsSiteAdmin();
  const audience = useQuery({
    queryKey: ['newsletter-audience'],
    queryFn: () => api.get<{ count: number }>('/admin/newsletter/audience'),
    enabled: isAdmin,
  });
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [preview, setPreview] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [topic, setTopic] = useState('');
  const [points, setPoints] = useState('');
  const [tone, setTone] = useState('professional and friendly');
  const [withImage, setWithImage] = useState(true);

  const generate = useMutation({
    mutationFn: () => api.post<{ subject: string; bodyHtml: string; imageUrl: string | null }>(
      '/admin/newsletter/generate', { topic: topic.trim(), points: points.trim() || undefined, tone, image: withImage }),
    onSuccess: (r) => { setErr(null); setSubject(r.subject); setBodyHtml(r.bodyHtml); setPreview(true); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'AI generation failed'),
  });

  const testSend = useMutation({
    mutationFn: () => api.post<{ to: string; sent: number }>('/admin/newsletter/test', { subject, bodyHtml }),
    onSuccess: (r) => { setErr(null); setResult(`Test sent to ${r.to} — check your inbox.`); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Test send failed'),
  });

  const send = useMutation({
    mutationFn: () => api.post<{ recipients: number; sent: number }>('/admin/newsletter/send', { subject, bodyHtml }),
    onSuccess: (r) => { setErr(null); setResult(`Sent to ${r.sent} of ${r.recipients} recipient(s).`); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Send failed'),
  });

  if (!isAdmin) {
    return <div className="panel empty"><h3>Restricted</h3><p>The newsletter tool is available to Lakota platform staff only.</p></div>;
  }

  const count = audience.data?.count ?? 0;
  const canSend = subject.trim() && bodyHtml.trim() && !send.isPending;

  function confirmSend() {
    setResult(null); setErr(null);
    if (window.confirm(`Send this newsletter to ${count} subscribed recipient(s)? This cannot be undone.`)) {
      send.mutate();
    }
  }

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Newsletter</h1>
          <div className="sub">
            Send a newsletter to all active, subscribed customers ({audience.isLoading ? '…' : count} recipient{count === 1 ? '' : 's'}).
            Each email includes an unsubscribe link; transactional emails are unaffected.
          </div>
        </div>
      </div>

      {result && <div className="banner-ok" style={{ marginBottom: 16 }}>✓ {result}</div>}
      {err && <p className="cart-error">{err}</p>}

      <div className="section-title">✨ Draft with AI</div>
      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="demo-grid">
          <label className="field" style={{ gridColumn: '1 / -1' }}><span className="field-label">What's this newsletter about?</span>
            <input className="input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. New self-service scanner licensing + secure EFT storage" /></label>
          <label className="field" style={{ gridColumn: '1 / -1' }}><span className="field-label">Key points <span className="opt">(optional, one per line)</span></span>
            <textarea className="input" rows={4} value={points} onChange={(e) => setPoints(e.target.value)}
              placeholder={'Add/remove scanners anytime\nStorage plans from $9.99/mo\nIn-app support'} /></label>
          <label className="field"><span className="field-label">Tone</span>
            <select className="select" value={tone} onChange={(e) => setTone(e.target.value)}>
              <option>professional and friendly</option>
              <option>concise and direct</option>
              <option>warm and appreciative</option>
              <option>excited announcement</option>
            </select></label>
          <label className="checkbox-row" style={{ alignSelf: 'end', paddingBottom: 10 }}>
            <input type="checkbox" checked={withImage} onChange={(e) => setWithImage(e.target.checked)} />
            <span>Generate a hero image</span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={() => { setErr(null); generate.mutate(); }} disabled={!topic.trim() || generate.isPending}>
            {generate.isPending ? (withImage ? 'Writing + rendering…' : 'Writing…') : '✨ Generate draft'}
          </button>
          <span className="field-help">Fills the subject &amp; body below — review and edit before sending. Nothing is sent automatically.</span>
        </div>
      </div>

      <div className="section-title">Compose &amp; send</div>
      <div className="panel">
        <div className="demo-grid">
          <label className="field" style={{ gridColumn: '1 / -1' }}><span className="field-label">Subject</span>
            <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. EFTCreator — What's new this month" /></label>
          <label className="field" style={{ gridColumn: '1 / -1' }}>
            <span className="field-label">Body (HTML)</span>
            <textarea className="input" rows={12} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}
              placeholder="<h2>Hello!</h2><p>Your update here…</p>" />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost" onClick={() => setPreview((p) => !p)} disabled={!bodyHtml.trim()}>
            {preview ? 'Hide preview' : 'Preview'}
          </button>
          <button className="btn btn-ghost" onClick={() => { setResult(null); setErr(null); testSend.mutate(); }}
            disabled={!subject.trim() || !bodyHtml.trim() || testSend.isPending}>
            {testSend.isPending ? 'Sending test…' : 'Send test to myself'}
          </button>
          <button className="btn btn-primary" onClick={confirmSend} disabled={!canSend}>
            {send.isPending ? 'Sending…' : `Send to ${count} recipient${count === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      {preview && bodyHtml.trim() && (
        <div style={{ marginTop: 18 }}>
          <div className="section-title">Preview</div>
          <div className="panel">
            <div style={{ fontSize: 15, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: bodyHtml }} />
          </div>
        </div>
      )}
    </div>
  );
}
